import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Image as ImageIcon,
  Close as CloseIcon,
  BrokenImage as BrokenImageIcon,
  DescriptionOutlined,
  TableChartOutlined,
  LaunchOutlined,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";

import { recordHome, recordTitle } from "src/api/records";
import { registerChunkIdToFetch } from "../../hooks/useCitationSources";
import { useOnboardingStore } from "../../stores/OnboardingStore";
import { reportOnboardingEvent } from "../../services/OnboardingService";
import { useUIStore } from "../../stores/UIStore";
import {
  useCitationPresentation,
  TABULAR_DATASET_TYPE_IDS,
} from "src/reusable/Chat/citations/CitationPresentationContext";
import {
  isImageSource,
  getImageContentTypeLabel,
} from "../../utils/imageSourceUtils";
import { useImageArtifact } from "../../hooks/useImageArtifact";
import {
  SOURCE_CLASSIFICATION_LABELS,
  type SourceClassification,
} from "src/views/datasets/documentAnnotations";
import { useSourceViewerStore } from "../../stores/sourceViewerStore";
import {
  useDialogueProvenance,
  useProvenanceDialogueId,
} from "../../services/ProvenanceService";
import { buildDocumentAnnotations } from "../sources/buildDocumentAnnotations";
import { safeHref } from "../sources/safeHref";
import { formatAgentPath } from "../sources/formatAgentPath";
import { externalProviderLabel } from "../sources/ExternalSourceTier";

type CiteGroupPosition = "single" | "start" | "middle" | "end";

/**
 * Format a chunk's page/row info for display, e.g. ", p. 3", ", p. 2-4",
 * or " (rows 5–9, Sheet1)". Handles both scalar and array `page_number`
 * (#1900). Exported for unit testing.
 */
export const formatPageInfo = (cm: any): string => {
  if (!cm) return "";

  // Tabular chunks (CSV/XLSX) carry row ranges instead of real pages.
  // Render "(rows N–M, SheetX)" — the synthetic page_number=chunk_index+1
  // would otherwise show as a misleading "p. 1" for every chunk.
  if (cm.start_row != null && cm.end_row != null) {
    const range =
      cm.start_row === cm.end_row
        ? `row ${cm.start_row}`
        : `rows ${cm.start_row}–${cm.end_row}`;
    return cm.sheet_name ? ` (${range}, ${cm.sheet_name})` : ` (${range})`;
  }

  if (cm.page_number == null) return "";
  const pageNumbers = Array.isArray(cm.page_number)
    ? cm.page_number
    : [cm.page_number];
  if (pageNumbers.length === 0) return "";

  // Backend stores 1-indexed page numbers; display directly.
  const first = pageNumbers[0];
  const last = pageNumbers[pageNumbers.length - 1];
  return first === last ? `, p. ${first}` : `, p. ${first}-${last}`;
};

type CitationLinkProps = {
  /** Gap-aware citation number; null for an unmatched cite (renders unnumbered). */
  citationNumber: number | null;
  hasSource: boolean;
  sourceData?: any;
  chunkMap?: Map<number, any>;
  citationsLoading?: boolean;
  /** Retained for call-site compat; viewer annotations are dialogue-scoped. */
  messageId?: string;
  /**
   * Position within a run of adjacent citations (e.g. `[1][4]`). Drives the
   * "stacked" treatment: members render flush with segmented corners so a run
   * reads as one multi-source marker. Defaults to "single".
   */
  citeGroupPosition?: CiteGroupPosition;
  children: React.ReactNode;
};

const CitationLink: React.FC<CitationLinkProps> = ({
  citationNumber,
  hasSource,
  sourceData,
  chunkMap,
  citationsLoading,
  citeGroupPosition = "single",
}) => {
  const { setRightSidebar, setScrollTargetSource } = useUIStore();
  const citationPresentation = useCitationPresentation();
  const openSourceInViewer = useSourceViewerStore((s) => s.openSource);
  // The DIALOGUE-scope provenance — the same react-query key the Sources panel
  // uses (already cached, no extra fetch) — so a citation click opens the
  // viewer with the WHOLE document's annotations under the SAME numbering the
  // panel shows. Message-scope provenance numbered the same document
  // differently per entry path (#20423 audit).
  const dialogueId = useProvenanceDialogueId();
  const { data: dialogueProvenance } = useDialogueProvenance(dialogueId);
  const [modalOpen, setModalOpen] = useState(false);

  // Check if this is an image source and get artifact ID
  const chunkMetadata =
    sourceData?.chunk_metadata ||
    chunkMap?.get(sourceData?.id)?.data_chunk?.chunk_metadata;
  const isImage = isImageSource(chunkMetadata);
  const imageArtifactId = isImage ? chunkMetadata?.image_artifact_id : null;
  const imageContentType = isImage
    ? sourceData?.chunk_data?.content_type ||
      chunkMap?.get(sourceData?.id)?.data_chunk?.chunk_data?.content_type
    : null;

  // Fetch image for preview (only if it's an image source)
  const { data: imageUrl } = useImageArtifact(imageArtifactId);

  // Open the sources tab and signal which card to scroll to. The actual
  // scroll fires from a layout-aware effect inside SourcesChunkView so we
  // don't have to guess when the DOM is ready or which scroll container
  // to walk up to.
  const scrollToSource = useCallback(
    (num: number) => {
      setRightSidebar({
        selectedItem: "sources",
        isOpen: true,
        isArtifactItemOpen: false,
        currentArtifactId: null,
      });
      setScrollTargetSource(num);
    },
    [setRightSidebar, setScrollTargetSource],
  );

  // Register chunk ID if it needs fetching
  useEffect(() => {
    if (sourceData?.needsFetch && sourceData?.id) {
      registerChunkIdToFetch(sourceData.id);
    }
  }, [sourceData?.needsFetch, sourceData?.id]);

  // Get the actual source data to use (batched fetched data or original)
  const actualSourceData = useMemo(() => {
    // If this citation needs fetching and we have batched data
    if (
      sourceData?.needsFetch &&
      sourceData?.id &&
      chunkMap?.has(sourceData.id)
    ) {
      const fetchedChunk = chunkMap.get(sourceData.id);

      // All sources (SSE and API) are now in SearchResult format
      const sourceRecord = fetchedChunk.source_data_record?.data || {};
      const metadata = sourceRecord.extracted_metadata || {};
      const sourceUid = fetchedChunk.source_data_record?.source_uid;

      const cm = fetchedChunk.data_chunk?.chunk_metadata || {};
      return {
        id: fetchedChunk.data_chunk.id,
        source_uid: sourceUid,
        sdr_id: fetchedChunk.source_data_record?.id,
        dataset_id:
          fetchedChunk.dataset?.id ??
          fetchedChunk.source_data_record?.dataset_fk,
        // Folder-scoped SDRs (project files) carry no dataset id; the AA
        // Projects presentation resolves these into its file rail.
        folder_id: fetchedChunk.source_data_record?.project_folder_fk,
        dataset_name: fetchedChunk.dataset?.name,
        dataset_type_id:
          fetchedChunk.dataset?.dataset_type_id ??
          fetchedChunk.dataset?.dataset_type?.id,
        data: {
          file_name: sourceRecord.file_name || "document",
          extracted_metadata: {
            title:
              metadata.title ||
              sourceRecord.file_name ||
              sourceUid ||
              "Document",
          },
        },
        chunk_metadata: {
          page_number: cm.page_number,
          start_row: cm.start_row,
          end_row: cm.end_row,
          sheet_name: cm.sheet_name,
        },
        rawData: sourceRecord,
        extractedMetadata: metadata, // Store extracted metadata separately for hover
        chunkText: fetchedChunk.data_chunk?.chunk_data?.data || "", // Store the raw chunk text
        // Source-provenance fields (#20423) — /chunks/by-ids returns these at
        // the row top level; carry them onto the normalized source so the hover
        // tooltip can render the classification chip, agent badge, revision,
        // retrieval timestamp, and the originating query.
        classification: fetchedChunk.classification,
        retrieved_by_agent: fetchedChunk.retrieved_by_agent,
        // Earliest retrieval event ref; its tool_name is internal-only and is
        // rendered ONLY via formatAgentPath's provider labels (never raw).
        primary_retrieval: fetchedChunk.primary_retrieval,
        revision: fetchedChunk.revision,
        retrieved_at: fetchedChunk.retrieved_at,
        retrieval_query: fetchedChunk.retrieval_query,
      };
    }

    // Source data from rehype is already normalized
    return sourceData;
  }, [sourceData, chunkMap]);

  // Tabular datasets store one SourceDataRecord per row, so source_uid is the
  // row identifier. These render as "{dataset} | #{source_uid}" instead of the
  // parenthesized document-style "(title, p. N)".
  const isTabularRow =
    !!actualSourceData?.source_uid &&
    actualSourceData?.dataset_type_id != null &&
    TABULAR_DATASET_TYPE_IDS.includes(actualSourceData.dataset_type_id);

  // A citation is actionable when its source is in the current response
  // (hasSource) or — for the presentation/click-out path — once we have a
  // dataset id to deep-link to. The latter covers lazy-fetched citations,
  // whose chunk data resolves after first render while hasSource stays false.
  // Folder-scoped sources (project files) have no dataset id; they present
  // via folder_id instead. Presentations with their own sources panel
  // (handlesInResponseSources) additionally claim hasSource clicks that have
  // no id to deep-link to.
  const canPresent =
    !!citationPresentation &&
    (!!actualSourceData?.dataset_id ||
      actualSourceData?.folder_id != null ||
      (citationPresentation.handlesInResponseSources === true && hasSource));
  // External library citations have no chunk/dataset to open in the viewer;
  // clicking one opens its source url in a new tab. The backend sanitizer only
  // covers provenance-payload externals — live-overlay externals arrive
  // straight from the streamed tool_response — so the http(s) scheme is
  // re-checked here before the URL can ever reach window.open.
  const externalUrl =
    actualSourceData?.isExternal && typeof actualSourceData.url === "string"
      ? (safeHref(actualSourceData.url) ?? null)
      : null;
  const isClickable = hasSource || canPresent || !!externalUrl;

  // Touch two-tap (spec §11.2): on a touch device the first tap reveals the
  // tooltip and the second tap opens the viewer, so a tap can't skip the
  // metadata. Desktop hover/click is unchanged. `wasTouchRef` is set by the
  // touchstart that precedes a synthetic click; `tooltipOpen` controls the MUI
  // tooltip (hover still drives it via onOpen/onClose).
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const wasTouchRef = useRef(false);

  const handleCitationClick = () => {
    if (!isClickable) return;

    // Touch: first tap shows the tooltip and stops here; second tap falls
    // through to open. A tap with no tooltip content opens immediately (nothing
    // to reveal).
    if (wasTouchRef.current) {
      wasTouchRef.current = false;
      if (hoverPreview && !tooltipOpen) {
        setTooltipOpen(true);
        return;
      }
      setTooltipOpen(false);
    }

    // Track citation clicks during onboarding for telemetry
    const { isOnboarding, onboardingDialogueId } =
      useOnboardingStore.getState();
    if (isOnboarding) {
      reportOnboardingEvent("citation_clicked", {
        citation_number: citationNumber,
        dialogue_id: onboardingDialogueId,
      }).catch(() => {
        // Best-effort telemetry — never block UI.
      });
    }

    // External library source: open its sanitized url in a new tab. No viewer
    // modal / sidebar (there is no chunk or dataset to resolve).
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // When a presentation context is provided (e.g. Equipment AI's sidebar
    // chat, which has no sources sidebar), defer to it instead of opening the
    // main Assistant right sidebar. A presentation may return false to
    // DECLINE the click (AA Projects always does, after its own tree-highlight
    // side effect), falling through to the default in-chat viewer below.
    if (canPresent) {
      const handled = citationPresentation!.openCitation({
        citationNumber,
        sourceUid: actualSourceData?.source_uid,
        sdrId: actualSourceData?.sdr_id,
        datasetId: actualSourceData?.dataset_id,
        datasetTypeId: actualSourceData?.dataset_type_id,
        pageNumber: actualSourceData?.chunk_metadata?.page_number,
        // The cited chunk, so record-view presentations can scroll to the
        // cited passage (#2378), not just the page.
        chunkId: actualSourceData?.id,
        folderId: actualSourceData?.folder_id,
        fileName: actualSourceData?.data?.file_name,
        hasSource,
      });
      if (handled !== false) return;
    }

    // Default (standalone /assistant chat, and any presentation host that
    // declines): clicking a citation auto-opens the document viewer modal
    // scrolled to this citation's passage, with that passage highlighted as
    // cited — same overlay the Sources cards open (#20423). We also flip the
    // right sidebar to the Sources tab + mark the scroll target, so closing
    // the modal lands on the cited source in the panel.
    //
    // Gated on a resolvable home (dataset or folder id), not just hasSource:
    // a lazy-fetched citation (its chunk resolved via chunkMap, not yet in the
    // current message's responseSources) has a real home to open but
    // hasSource stays false — gating on hasSource alone silently dropped the
    // click for any host presentation that declines unconditionally (e.g.
    // ChatPanePanel's, which always defers here).
    const datasetId = actualSourceData?.dataset_id;
    const folderId = actualSourceData?.folder_id;
    if (hasSource || datasetId != null || folderId != null) {
      if (citationNumber != null) {
        scrollToSource(citationNumber);
      }

      const sdrId = actualSourceData?.sdr_id;
      const chunkId = actualSourceData?.id;
      if (sdrId != null && chunkId != null) {
        const pn = actualSourceData?.chunk_metadata?.page_number;
        const page = Array.isArray(pn) ? pn[0] : (pn ?? null);
        // Prefer the document's FULL cited + read annotations from the
        // dialogue provenance — the exact payload the Sources panel builds its
        // annotations from — so viewer badge numbers match the panel entry
        // path. Fall back to a single cited annotation for the clicked passage
        // if provenance hasn't resolved yet (the viewer enriches it with real
        // bounding boxes from its own fetched chunk data).
        const fullAnnotations = dialogueProvenance
          ? buildDocumentAnnotations(dialogueProvenance, sdrId)
          : [];
        const annotations =
          fullAnnotations.length > 0
            ? fullAnnotations
            : [
                {
                  chunk_id: chunkId,
                  page: page ?? 1,
                  cited: true,
                  ...(citationNumber != null ? { citationNumber } : {}),
                },
              ];
        // Through recordHome(), not built by hand: a replicated dataset's
        // citation carries a negative dataset_id, and recordHome() is what
        // normalizes that (Math.abs) — every other RecordHome in the app goes
        // through it for exactly this reason. Constructing the Dataset case
        // directly here would hand the drawer a negative id where it expects
        // the normalized one (the store, InChatDocumentViewer's useGetDataset,
        // etc. all assume that contract), breaking this default open path for
        // any citation into a replicated dataset.
        //
        // folderId is authoritative when present: a citation can carry a
        // dataset id alongside a real folder id (e.g. a display/grouping
        // dataset unrelated to the SDR's actual scope), and recordHome()
        // otherwise prioritizes any dataset FK it's handed, opening the wrong
        // dataset instead of the folder file.
        const home = recordHome({
          dataset_fk: folderId == null ? datasetId : undefined,
          project_folder_fk: folderId,
        });
        if (home) {
          openSourceInViewer({
            home,
            sdrId,
            page,
            chunkId,
            annotations,
            // Carry provenance so the viewer can surface cross-reference links.
            provenance: dialogueProvenance,
          });
        }
      }
    }
  };

  // Get page (or row, for tabular) information
  const getPageInfo = () => formatPageInfo(actualSourceData?.chunk_metadata);

  // Get document title for citation
  const getCitationDescription = () => {
    if (citationsLoading && sourceData?.needsFetch) {
      return `Loading...`;
    }

    // Tabular row: "{dataset} | #{source_uid}" (the paren wrapper is dropped
    // for these at render time).
    if (isTabularRow) {
      const ds = actualSourceData.dataset_name;
      return ds
        ? `${ds} | #${actualSourceData.source_uid}`
        : `#${actualSourceData.source_uid}`;
    }

    // If we have source data, use the title (which should be guaranteed to exist)
    if (actualSourceData) {
      const pageInfo = getPageInfo();
      const title = recordTitle(actualSourceData);

      if (title && title.trim()) {
        // Intelligently truncate long titles (leaving room for page info)
        const maxLength = 70 - pageInfo.length;
        if (title.length > maxLength) {
          const colonIndex = title.indexOf(":");
          if (colonIndex > 0 && colonIndex < maxLength - 10) {
            return title.substring(0, colonIndex) + pageInfo;
          }
          return title.substring(0, maxLength - 3) + "..." + pageInfo;
        }
        return title + pageInfo;
      } else {
        return `Document ${citationNumber || "?"}${pageInfo}`;
      }
    }

    // For unmatched citations, still show something
    // Removed noisy debug log: "No actualSourceData, using basic fallback"
    return `Reference ${citationNumber || "?"}`;
  };

  // Source-provenance fields (spec #20423 Story 2). `classification`,
  // `retrieved_by_agent`, `revision`, `retrieved_at`, and `retrieval_query` now
  // ride on the source/citation item top-level (the backend serializer sends
  // them on /chunks/by-ids rows too). We PREFER those top-level fields and keep
  // the chunk_metadata reads as a defensive fallback for older payloads.
  const getProvenanceInfo = () => {
    const src = actualSourceData || {};
    const cm =
      src.chunk_metadata ||
      chunkMap?.get(src.id)?.data_chunk?.chunk_metadata ||
      {};

    const classification = (src.classification ||
      src.rawData?.classification) as SourceClassification | undefined;
    const classificationLabel =
      classification && classification in SOURCE_CLASSIFICATION_LABELS
        ? SOURCE_CLASSIFICATION_LABELS[classification]
        : null;

    const retrievedByAgent =
      src.retrieved_by_agent ?? src.rawData?.retrieved_by_agent ?? null;
    const primaryRetrieval =
      src.primary_retrieval ?? src.rawData?.primary_retrieval ?? null;

    // Prefer the backend's top-level `revision`; fall back to chunk_metadata
    // spellings the extractor may use.
    const revision =
      src.revision ??
      src.rawData?.revision ??
      cm.revision ??
      cm.version ??
      cm.rev ??
      src.extractedMetadata?.revision;
    const section =
      cm.section ?? cm.section_title ?? src.extractedMetadata?.section;

    // Retrieval timestamp + query (top-level provenance fields).
    const retrievedAtRaw = src.retrieved_at ?? src.rawData?.retrieved_at;
    let retrievedAt: string | null = null;
    if (retrievedAtRaw) {
      const d = new Date(retrievedAtRaw);
      retrievedAt = Number.isNaN(d.getTime())
        ? String(retrievedAtRaw)
        : format(d, "MMM d, yyyy p");
    }
    const retrievalQuery =
      src.retrieval_query ?? src.rawData?.retrieval_query ?? null;

    return {
      classificationLabel,
      retrievedByAgent,
      primaryRetrieval,
      revision,
      section,
      retrievedAt,
      retrievalQuery,
    };
  };

  // Create hover preview content
  const createHoverPreview = () => {
    if (!actualSourceData) return null;

    // Show loading state
    if (citationsLoading && sourceData?.needsFetch) {
      return (
        <Box sx={{ maxWidth: 300, p: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Loading chunk data...
          </Typography>
        </Box>
      );
    }

    // External library source preview (#20423 A2): provider chip + title +
    // identifier + snippet, and a launch affordance since clicking opens the
    // source url in a new tab (there is no in-app viewer for externals).
    if (actualSourceData.isExternal) {
      const providerLabel = externalProviderLabel(actualSourceData.source_type);
      const extTitle = recordTitle(actualSourceData) || "External source";
      return (
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <LaunchOutlined
              sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {extTitle}
            </Typography>
          </Stack>

          <Chip
            label={providerLabel}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ mb: 1, height: "20px", fontSize: "0.68rem" }}
          />

          {actualSourceData.identifier && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, wordBreak: "break-word" }}
            >
              {actualSourceData.identifier}
            </Typography>
          )}

          {actualSourceData.chunkText && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                fontSize: "0.8rem",
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
              }}
            >
              {actualSourceData.chunkText}
            </Typography>
          )}

          {externalUrl && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block", fontStyle: "italic" }}
            >
              Click to open source ↗
            </Typography>
          )}
        </Box>
      );
    }

    // Image source preview
    if (isImage && imageArtifactId) {
      const title = recordTitle(actualSourceData) || "";
      const pageInfo = getPageInfo();

      return (
        <Box sx={{ p: 2, maxWidth: 320 }}>
          {/* Image type chip */}
          <Chip
            icon={<ImageIcon sx={{ fontSize: "0.9rem" }} />}
            label={getImageContentTypeLabel(imageContentType)}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ mb: 1.5, height: "22px", fontSize: "0.7rem" }}
          />

          {/* Image thumbnail */}
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Source image"
              sx={{
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                mb: 1.5,
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: 100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "action.hover",
                borderRadius: 1,
                mb: 1.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Loading image...
              </Typography>
            </Box>
          )}

          {/* Document context */}
          {title && (
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
          )}
          {pageInfo && (
            <Typography variant="caption" color="text.secondary">
              {pageInfo.replace(/^,\s*/, "")}
            </Typography>
          )}

          {/* VLM description excerpt */}
          {actualSourceData.chunkText && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {actualSourceData.chunkText}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", fontStyle: "italic" }}
          >
            Click to view in sources
          </Typography>
        </Box>
      );
    }

    // Get metadata from the source (non-image)
    const metadata =
      actualSourceData.extractedMetadata ||
      actualSourceData.rawData?.extracted_metadata ||
      actualSourceData.data?.extracted_metadata ||
      {};

    // A tabular row is not a document: title it by its dataset (with the row id
    // surfaced below), not the "Document" file-name fallback.
    const title = isTabularRow
      ? actualSourceData.dataset_name || "Dataset row"
      : recordTitle(actualSourceData) || "Document";
    const author = metadata.author;
    const publicationDate = metadata.publication_date;
    const description = metadata.description;
    const pageInfo = getPageInfo();
    const {
      classificationLabel,
      retrievedByAgent,
      primaryRetrieval,
      revision,
      section,
      retrievedAt,
      retrievalQuery,
    } = getProvenanceInfo();

    // Labeled metadata rows in the mockup's order (Section · Page · Revision ·
    // Via · Retrieved · Query), then any extra fields the extractor captured.
    // Empty fields are omitted. "Via" folds in the retrieving agent (multi-agent
    // provenance); Retrieved/Query come from the RetrievalEvent seam (#20423).
    const previewItems = [];

    if (section) {
      previewItems.push({ label: "Section", value: String(section) });
    }

    if (isTabularRow && actualSourceData.source_uid) {
      // Tabular rows are located by their row identifier, not a page.
      previewItems.push({
        label: "Row",
        value: `#${actualSourceData.source_uid}`,
      });
    } else if (pageInfo) {
      previewItems.push({
        label: "Page",
        value: pageInfo.replace(/^,\s*/, ""),
      });
    }

    if (revision) {
      previewItems.push({ label: "Revision", value: String(revision) });
    }

    // "Via" shows the agent path ("AtomAssist (direct)" / "{agent} · ADAMS" /
    // "{agent} · OSTI") — shown whenever the source carries retrieval
    // attribution, so legacy payloads without provenance keep omitting the row.
    if (retrievedByAgent || primaryRetrieval) {
      previewItems.push({
        label: "Via",
        value: formatAgentPath({
          retrieved_by_agent: retrievedByAgent,
          primary_retrieval: primaryRetrieval,
        }),
      });
    }

    if (retrievedAt) {
      previewItems.push({ label: "Retrieved", value: retrievedAt });
    }

    if (retrievalQuery) {
      previewItems.push({ label: "Query", value: String(retrievalQuery) });
    }

    if (author) {
      previewItems.push({ label: "Author", value: author });
    }

    if (publicationDate) {
      previewItems.push({ label: "Date", value: publicationDate });
    }

    return (
      <Box sx={{ p: 2, maxWidth: 300 }}>
        {/* Header: source-type icon + title (mockup's cite-tooltip header row).
            Tabular rows get a table icon so they don't read as a document. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {isTabularRow ? (
            <TableChartOutlined
              sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }}
            />
          ) : (
            <DescriptionOutlined
              sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }}
            />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {title}
          </Typography>
        </Stack>

        {/* Source-classification chip (Project file / Org dataset / …) */}
        {classificationLabel && (
          <Chip
            label={classificationLabel}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ mb: 1, height: "20px", fontSize: "0.68rem" }}
          />
        )}

        {previewItems.length > 0 && (
          <>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ mb: 1 }}>
              {previewItems.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 0.5,
                    alignItems: "baseline",
                  }}
                >
                  {/* Uppercase mini-label matching the mockup's cite-tooltip */}
                  <Typography
                    sx={{
                      minWidth: 56,
                      flexShrink: 0,
                      fontSize: "0.625rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "text.secondary",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        {description && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              {description.length > 150
                ? description.substring(0, 150) + "..."
                : description}
            </Typography>
          </Box>
        )}

        {/* Show raw chunk text if available */}
        {actualSourceData.chunkText && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Content excerpt:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.4,
              }}
            >
              {actualSourceData.chunkText}
            </Typography>
          </Box>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block", fontStyle: "italic" }}
        >
          Click to view in sources
        </Typography>
      </Box>
    );
  };

  let citationDescription = getCitationDescription();

  // If no citation description (null), don't render anything
  if (citationDescription === null) {
    return null;
  }

  // Safety check: never show raw JSON. Use "?" when the cite is unmatched
  // (citationNumber null) so we don't render "Source null" (#20423 L4).
  if (citationDescription.includes("{") || citationDescription.includes("[")) {
    citationDescription = `Source ${citationNumber ?? "?"}`;
  }

  const hoverPreview = createHoverPreview();

  // Stacked-citation treatment: a run of adjacent markers (e.g. [1][4]) renders
  // flush with segmented corners and a thin divider between members, so it reads
  // as one multi-source marker instead of separate pills (chat-v2 spec).
  const isGrouped = citeGroupPosition !== "single";
  const groupedRadius: Record<CiteGroupPosition, string> = {
    single: "4px",
    start: "4px 0 0 4px",
    middle: "0",
    end: "0 4px 4px 0",
  };
  const groupedMargin: Record<CiteGroupPosition, string> = {
    single: "0 1px",
    start: "0 0 0 1px",
    middle: "0",
    end: "0 1px 0 0",
  };

  const citationElement = (
    <Box
      component="span"
      data-testid="inline-citation"
      data-cite-group-position={citeGroupPosition}
      data-attr={`assistant:link:citation-${citationNumber ?? "unresolved"}`}
      onClick={handleCitationClick}
      onTouchStart={() => {
        wasTouchRef.current = true;
      }}
      sx={{
        cursor: isClickable ? "pointer" : "default",
        color: isClickable
          ? isImage
            ? "secondary.main"
            : "primary.main"
          : "text.disabled",
        backgroundColor: isClickable
          ? isImage
            ? "rgba(156, 39, 176, 0.06)"
            : "rgba(25, 118, 210, 0.06)"
          : "rgba(0, 0, 0, 0.03)",
        // Compact numbered superscript badge (matches the source-provenance
        // mockup): the inline marker shows just the citation number; the full
        // "(title, section, page)" metadata lives in the hover tooltip + aria.
        borderRadius: groupedRadius[citeGroupPosition],
        // Divide adjacent members of a stack so [1][4] doesn't read as "14".
        borderLeft:
          isGrouped && citeGroupPosition !== "start"
            ? "1px solid rgba(25, 118, 210, 0.35)"
            : "none",
        padding: "0 4px",
        margin: groupedMargin[citeGroupPosition],
        minWidth: "16px",
        justifyContent: "center",
        textDecoration: "none",
        transition: "all 0.15s ease",
        "&:hover": isClickable
          ? {
              backgroundColor: isImage
                ? "rgba(156, 39, 176, 0.18)"
                : "rgba(25, 118, 210, 0.18)",
            }
          : {},
        fontSize: "0.7em",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        verticalAlign: "super",
        lineHeight: 1.5,
      }}
      aria-label={citationDescription}
    >
      {isImage && (
        <ImageIcon sx={{ fontSize: "0.85em", verticalAlign: "middle" }} />
      )}
      {/* Unmatched cites render an unnumbered marker (bullet) — never a
          fabricated [1]. The tooltip/aria description still identifies it. */}
      {citationNumber ?? "•"}
    </Box>
  );

  // Wrap citation with tooltip if we have content to show
  const citationWithTooltip = hoverPreview ? (
    <Tooltip
      title={hoverPreview}
      arrow
      placement="top"
      // Controlled so touch two-tap (handleCitationClick) can open it on the
      // first tap; hover/focus still drive it on desktop via onOpen/onClose.
      // MUI's OWN touch listeners must be off (#20423 audit 7): its long-press
      // (enterTouchDelay) would open the tooltip through onOpen so the first
      // perceived tap navigates, and its leaveTouchDelay timer would auto-close
      // before a slow second tap — both defeat the gate. With them disabled,
      // touch is driven ONLY by the click gate; tap-away still closes via the
      // focus/blur listeners.
      disableTouchListener
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 2,
            "& .MuiTooltip-arrow": {
              color: "background.paper",
            },
          },
        },
      }}
    >
      {citationElement}
    </Tooltip>
  ) : (
    citationElement
  );

  // For non-image sources, just return the citation
  if (!isImage || !imageArtifactId) {
    return citationWithTooltip;
  }

  // Get document context for inline display
  const title = recordTitle(actualSourceData) || "";
  const pageInfo = getPageInfo();

  // Render inline image display for image sources
  return (
    <Box
      component="span"
      data-testid={`aa-citation-inline-image-${citationNumber}`}
      sx={{ display: "inline" }}
    >
      {/* Citation pill */}
      {citationWithTooltip}

      {/* Inline image display */}
      <Box
        sx={{
          display: "block",
          mt: 1.5,
          mb: 2,
          maxWidth: 400,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        {/* Image header with type chip */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "action.hover",
          }}
        >
          <Chip
            label={citationNumber ?? "•"}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{
              height: "22px",
              minWidth: "28px",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<ImageIcon sx={{ fontSize: "0.9rem" }} />}
            label={getImageContentTypeLabel(imageContentType)}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ height: "22px", fontSize: "0.7rem" }}
          />
          {title && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {title}
              {pageInfo && ` ${pageInfo.replace(/^,\s*/, "")}`}
            </Typography>
          )}
        </Stack>

        {/* Image content */}
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "center",
            cursor: imageUrl ? "pointer" : "default",
          }}
          onClick={() => imageUrl && setModalOpen(true)}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={`Source image ${citationNumber}`}
              data-testid={`aa-inline-image-${citationNumber}`}
              sx={{
                maxWidth: "100%",
                maxHeight: 250,
                objectFit: "contain",
                borderRadius: 1,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            />
          ) : citationsLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 120,
                width: "100%",
              }}
            >
              <CircularProgress size={24} color="secondary" />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 100,
                width: "100%",
                color: "text.secondary",
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="caption">Image unavailable</Typography>
            </Box>
          )}
        </Box>

        {/* VLM description excerpt */}
        {actualSourceData?.chunkText && (
          <Box
            sx={{
              px: 1.5,
              pb: 1.5,
              pt: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
                lineHeight: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {actualSourceData.chunkText}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Full-size image modal */}
      <Dialog
        data-testid={`aa-inline-image-modal-${citationNumber}`}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <IconButton
          data-testid={`aa-inline-image-modal-close-${citationNumber}`}
          data-attr={`assistant:btn:close-image-modal-${citationNumber}`}
          onClick={() => setModalOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {imageUrl && (
            <img
              data-testid={`aa-inline-image-modal-fullsize-${citationNumber}`}
              src={imageUrl}
              alt={`Full size - Source ${citationNumber}`}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CitationLink;
